import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import classNames from 'classnames';
import qs from 'qs';

import { showSnackbar } from 'helpers/snackbar';

import { app, media } from 'context';
import { APP_BANNER_HEIGHT, HEADER_Z_INDEX, IconButton, Layout, Text } from 'ds';
import { APP_PAGE_PADDING_X_MOBILE } from 'ds/PageLayout/constants';
import { AI_ASSISTANT_PATH, TENANT_ONBOARDING_CHECKLIST_PATH } from 'routes';
import { createUserAction } from 'shared/requests';
import { actions } from 'store/AIAssistant';
import { selectConversationsSidebarVisible } from 'store/AIAssistant/selectors';
import { actions as dashboardActions } from 'store/Dashboard';
import { selectDashboardWidgetConfigs } from 'store/Dashboard/selectors';
import { selectIsTenantUx, selectUIState } from 'store/UI/selectors';
import { actions as uiActions } from 'store/UI';
import { actions as userActions } from 'store/User';
import { selectUser, selectUserActions, selectUserId } from 'store/User/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectTenancyId } from 'store/v2/User/selectors';
import { showTenantOnboardingChecklist } from '../Onboarding/selectors';
import { TENANT_ONBOARDING_CHECKLIST_ACKNOWLEDGED_ACTION } from '../constants';

import AIAssistantConversation from './AIAssistantConversation';
import AIAssistantConversationInput from './AIAssistantConversationInput';
import AIAssistantConversations from './AIAssistantConversations';
import AIAssistantPromptSuggestionCard from './AIAssistantPromptSuggestionCard';
import KickoffModal from './KickoffModal';
import { fetchConversations, postToConversation, startConversation } from './requests';
import Widgets from '../Dashboard/Widgets';
import { getDashboardWidgetConfigs } from '../Dashboard/requests';
import { KICKOFF_MODAL_ACTION, SUGGESTED_AI_ASSISTANT_PROMPTS } from '../constants';
import OnboardingChecklistWelcomeModal from '../Onboarding/OnboardingChecklistWelcomeModal';

interface RouteParams {
  id?: string;
}

interface LocationState {
  autoFocus?: boolean;
  serviceMessage?: string;
}

const AIAssistant: React.FC = () => {
  const dispatch = useAppDispatch();
  const { appBannerType } = useAppSelector(selectUIState);
  const { isMobile } = useContext(media);
  const { bottomNavigationHeight } = useContext(app);
  const history = useHistory<LocationState>();
  const location = history.location;
  const autoFocus = location.state?.autoFocus;
  const serviceMessage = location.state?.serviceMessage;
  const isOnDashboard =
    location.pathname === AI_ASSISTANT_PATH || location.pathname.startsWith(AI_ASSISTANT_PATH + '/');
  const isOnChecklistPage = location.pathname === TENANT_ONBOARDING_CHECKLIST_PATH;
  const user = useAppSelector(selectUser);
  const tenancyId = useAppSelector(selectTenancyId);
  const isTenantUx = useAppSelector(selectIsTenantUx);
  const params = useParams<RouteParams>();
  const conversationId = params.id ? parseInt(params.id) : undefined;
  const [loadedFirstTime, setLoadedFirstTime] = useState(false);
  const { windowWidth } = useContext(app);
  const [greeting, _setGreeting] = useState('What can I help with today?');
  const isConversationsDrawerVisible = useAppSelector(selectConversationsSidebarVisible);
  const [sideBarIsAlreadyShown, setSideBarIsAlreadyShown] = useState(isConversationsDrawerVisible);
  const [showSuggestedPrompts, setShowSuggestedPrompts] = useState(false);
  const qsParams = qs.parse(history.location.search, { ignoreQueryPrefix: true });
  const hasKickoffParam = 'kickoff' in qsParams;
  const widgetConfigs = useAppSelector(selectDashboardWidgetConfigs);
  const existingUserActions = useAppSelector(selectUserActions);
  const viewedKickoffModal = existingUserActions.find(a => a.value === KICKOFF_MODAL_ACTION);
  const userId = useAppSelector(selectUserId);
  const shouldShowChecklist = useAppSelector(showTenantOnboardingChecklist);
  const hasAcknowledgedChecklist = !!existingUserActions.find(
    a => a.value === TENANT_ONBOARDING_CHECKLIST_ACKNOWLEDGED_ACTION
  );

  const [showKickoffModal, setShowKickoffModal] = useState<boolean>(false);
  const [showOnboardingChecklistModal, setShowOnboardingChecklistModal] = useState<boolean>(false);
  const [_areConversationsRendered, setAreConversationsRendered] = useState(false);
  const [isExitAnimationActive, setIsExitAnimationActive] = useState(false);
  const [shouldAnimateWidgetsAppear, setShouldAnimateWidgetsAppear] = useState(false);
  const threadInputFocusAreaRef = useRef<HTMLDivElement>(null);
  const widgetsContainerRef = useRef<HTMLDivElement>(null);
  const widgetsAppearTimeoutRef = useRef<number>();
  const conversationInputContainerMarginTop = 36;
  const conversationInputContainerMarginBottom = conversationId ? 36 : 0;

  const [suggestedPrompts, setSuggestedPrompts] = useState<string[]>([]);

  useEffect(() => {
    const initialPrompts = SUGGESTED_AI_ASSISTANT_PROMPTS.sort(() => Math.random() - 0.5).slice(0, 3);
    setSuggestedPrompts(initialPrompts);
  }, []);

  const WIDGETS_APPEAR_ANIMATION_MS = 150;

  const resetThreadInputFocusState = () => {
    setShowSuggestedPrompts(false);
    setIsExitAnimationActive(false);

    window.clearTimeout(widgetsAppearTimeoutRef.current);
    setShouldAnimateWidgetsAppear(false);

    window.requestAnimationFrame(() => {
      if (widgetsContainerRef.current) {
        void widgetsContainerRef.current.offsetHeight;
      }

      setShouldAnimateWidgetsAppear(true);
      widgetsAppearTimeoutRef.current = window.setTimeout(() => {
        setShouldAnimateWidgetsAppear(false);
      }, WIDGETS_APPEAR_ANIMATION_MS);
    });
  };

  const handleThreadInputFocus = () => {
    if (showSuggestedPrompts || conversationId !== undefined) return;

    window.clearTimeout(widgetsAppearTimeoutRef.current);
    setShouldAnimateWidgetsAppear(false);
    setShowSuggestedPrompts(true);
  };

  const handleThreadInputBlur = () => {
    if (conversationId !== undefined || !showSuggestedPrompts) return;
    if (isExitAnimationActive) return;

    // Defer so pill mousedown (with preventDefault) can run without resetting before click.
    window.requestAnimationFrame(() => {
      const activeElement = document.activeElement;
      if (activeElement && threadInputFocusAreaRef.current?.contains(activeElement)) return;
      resetThreadInputFocusState();
    });
  };

  const widgetsAnimationClassName = classNames({
    'animate-disappear': showSuggestedPrompts,
    'animate-appear': shouldAnimateWidgetsAppear
  });

  useEffect(() => {
    return () => {
      window.clearTimeout(widgetsAppearTimeoutRef.current);
    };
  }, []);

  const isWithinThreadInputFocusArea = (target: Node) => {
    if (threadInputFocusAreaRef.current?.contains(target)) return true;
    if (target instanceof Element && target.closest('.ai-assistant-suggestion-pill')) return true;
    return false;
  };

  useEffect(() => {
    if (!showSuggestedPrompts || conversationId !== undefined) return undefined;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (isWithinThreadInputFocusArea(target)) return;

      resetThreadInputFocusState();

      const inputEl = threadInputFocusAreaRef.current?.querySelector('.AIAssistant-threadInput');
      if (inputEl instanceof HTMLElement && document.activeElement === inputEl) {
        inputEl.blur();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [showSuggestedPrompts, conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasKickoffParam && !viewedKickoffModal) {
      setShowKickoffModal(true);
    }
  }, [hasKickoffParam, viewedKickoffModal]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        if (!tenancyId) return;

        const [conversationsResponse, widgetConfigsResponse] = await Promise.all([
          fetchConversations(tenancyId),
          getDashboardWidgetConfigs({ tenancyId })
        ]);

        dispatch(actions.setConversations(conversationsResponse));
        if (widgetConfigsResponse) {
          dispatch(dashboardActions.setWidgetConfigs(widgetConfigsResponse));
        }
      } catch (error) {
        showSnackbar({ message: 'Uh oh! There was an error connecting. Please try again later.', negative: true });
      }
    };

    loadInitialData();
  }, [dispatch, tenancyId]);

  useEffect(() => {
    if (user?.id && showKickoffModal) {
      createUserAction({ userId: user?.id, value: KICKOFF_MODAL_ACTION }).then(({ data }) => {
        dispatch(userActions.updateUser({ user_actions: data }));
      });
    }
  }, [showKickoffModal, dispatch, user?.id]);

  useEffect(() => {
    if (shouldShowChecklist && !hasAcknowledgedChecklist && userId && widgetConfigs?.length) {
      setShowOnboardingChecklistModal(true);
    }
  }, [shouldShowChecklist, hasAcknowledgedChecklist, userId, widgetConfigs?.length]);

  useEffect(() => {
    const autoSendServiceMessage = async () => {
      if (!serviceMessage || !tenancyId || conversationId !== undefined || !user?.id) {
        return;
      }

      // Check if we've already processed this serviceMessage to prevent duplicate conversations on back navigation
      // Use sessionStorage since refs are lost on component remount
      const storageKey = `processedServiceMessage_${serviceMessage}`;
      const alreadyProcessed = sessionStorage.getItem(storageKey);
      if (alreadyProcessed) {
        return;
      }

      try {
        // Mark this serviceMessage as processed in sessionStorage
        sessionStorage.setItem(storageKey, 'true');

        dispatch(actions.setNewConversationRequestState('in_progress'));
        const { data } = await startConversation(tenancyId);
        dispatch(actions.setRequestStateForConversation({ conversationId: data.id, requestState: 'in_progress' }));
        dispatch(actions.addConversation(data));
        dispatch(actions.addPendingConversationMessage({ conversationId: data.id, input: serviceMessage }));

        // Navigate immediately to the new conversation (before posting message, like onboarding flow)
        // Use replace to replace the /app/conversations entry so back button goes directly to services page
        history.replace(`${AI_ASSISTANT_PATH}/${data.id}`, {});

        const { data: conversation } = await postToConversation({
          tenancyId,
          conversationId: data.id,
          message: serviceMessage
        });

        dispatch(actions.removePendingConversationMessage({ conversationId: data.id }));
        dispatch(actions.updateConversation({ conversation }));
        dispatch(actions.setRequestStateForConversation({ conversationId: data.id, requestState: 'ready' }));
        dispatch(actions.setNewConversationRequestState('ready'));

        // Clear the sessionStorage key after successful conversation creation and navigation
        // This allows users to create new conversations with the same message if needed
        sessionStorage.removeItem(storageKey);
      } catch (error) {
        console.error(error);
        showSnackbar({ negative: true });
        dispatch(actions.setNewConversationRequestState('ready'));
        // Remove from sessionStorage on error so it can be retried
        sessionStorage.removeItem(storageKey);
      }
    };

    autoSendServiceMessage();
  }, [serviceMessage, tenancyId, conversationId, user?.id, dispatch, history]);

  const handleSkipChecklist = async () => {
    setShowOnboardingChecklistModal(false);
    if (userId) {
      const { data } = await createUserAction({
        userId,
        value: TENANT_ONBOARDING_CHECKLIST_ACKNOWLEDGED_ACTION
      });
      dispatch(userActions.updateUser({ user_actions: data }));
      if (isOnDashboard && !isOnChecklistPage) {
        dispatch(uiActions.updateUIState({ showChecklistSkipPopover: true }));
      }
    }
  };

  const handleCloseChecklistModal = () => {
    setShowOnboardingChecklistModal(false);
    if (userId && !hasAcknowledgedChecklist) {
      createUserAction({
        userId,
        value: TENANT_ONBOARDING_CHECKLIST_ACKNOWLEDGED_ACTION
      }).then(({ data }) => {
        dispatch(userActions.updateUser({ user_actions: data }));
      });
    }
  };

  const handleConversationsRendered = () => {
    setAreConversationsRendered(true);
  };

  if (!user || !widgetConfigs?.length) {
    return null;
  }

  if (!isTenantUx && !process.env.STORYBOOK) return null;

  // If we have a serviceMessage but no conversationId, we're in the process of creating a conversation
  // Don't render the conversations list - wait for navigation to complete
  // sessionStorage is used in useEffect to prevent duplicate conversations, but here we just check if we're creating
  const isCreatingConversation = !!serviceMessage && conversationId === undefined;

  // Return null immediately when creating conversation to prevent any UI flash
  if (isCreatingConversation) {
    return null;
  }

  if (isMobile) {
    return (
      <Layout
        direction="row"
        justify="center"
        align="center"
        color={conversationId === undefined ? 'blue-50' : 'white'}
        flexGrow={1}
        width="100%"
        minWidth={0}
        maxWidth="100%"
        className={isConversationsDrawerVisible ? 'content-shift' : 'content-reset'}
      >
        <KickoffModal isVisible={showKickoffModal} onClose={() => setShowKickoffModal(false)} />
        <OnboardingChecklistWelcomeModal
          isVisible={showOnboardingChecklistModal}
          onClose={handleCloseChecklistModal}
          onSkip={handleSkipChecklist}
        />
        {!isConversationsDrawerVisible && (
          <Layout
            position="fixed"
            left={0}
            top={appBannerType ? APP_BANNER_HEIGHT : 0}
            paddingLeft={12}
            paddingRight={12}
            paddingTop={12}
            color="white"
            zIndex={HEADER_Z_INDEX}
            width="100vw"
            justify="space-between"
            boxShadow={conversationId ? '2px 0px 22px 0px #57496D1F' : undefined}
            align="center"
          >
            <Layout direction="row" gap={12}>
              {conversationId !== undefined && (
                <IconButton
                  iconColor="gray-400"
                  name="leftChevron"
                  size="lg"
                  onClick={() => history.push(AI_ASSISTANT_PATH)}
                  type="noBackground"
                />
              )}
              <IconButton
                iconColor="gray-400"
                name="sort"
                size="lg"
                onClick={() => dispatch(actions.setSidebarVisible(true))}
                type="noBackground"
              />
            </Layout>
            {conversationId !== undefined && (
              <IconButton
                iconColor="gray-400"
                name="createPen"
                size="lg"
                onClick={() => history.push(AI_ASSISTANT_PATH)}
                type="noBackground"
              />
            )}
          </Layout>
        )}
        <AIAssistantConversations
          sideBarIsAlreadyShown={sideBarIsAlreadyShown}
          isVisible={isConversationsDrawerVisible}
          onClose={() => {
            setSideBarIsAlreadyShown(false);
            dispatch(actions.setSidebarVisible(false));
          }}
          loadedFirstTime={loadedFirstTime}
          setLoadedFirstTime={setLoadedFirstTime}
        />
        <Layout
          direction="column"
          justify="center"
          align="center"
          overflow="hidden"
          width="100%"
          minWidth={0}
          maxWidth="100%"
          className={classNames('AIAssistantThread', { 'is-threads-drawer-visible': isConversationsDrawerVisible })}
        >
          {conversationId && (
            <AIAssistantConversation conversationId={conversationId} onThreadsRendered={handleConversationsRendered} />
          )}
          {!conversationId && (
            <Layout
              direction="column"
              justify="center"
              align="center"
              paddingX={APP_PAGE_PADDING_X_MOBILE}
              marginTop={96}
              className={`greeting-layout ${isExitAnimationActive ? 'exit' : ''}`}
            >
              <Text size="headline-lg" bold align="center">
                Hi, {user?.firstname}.
              </Text>
              <Text size="headline-lg" bold color="purple-gradient" align="center">
                {greeting}
              </Text>
            </Layout>
          )}
          <Layout
            paddingX={APP_PAGE_PADDING_X_MOBILE}
            {...(conversationId !== undefined
              ? {
                  width: windowWidth,

                  left: 0,
                  paddingY: 12,
                  color: 'white',
                  borderTopLeftRadius: 16,
                  borderTopRightRadius: 16,
                  bottom: bottomNavigationHeight,
                  boxShadow: '0px 8px 24px 0px #0000001F',
                  position: 'fixed'
                }
              : { marginTop: 36, width: '100%', position: 'relative' })}
          >
            <Layout
              __ref={threadInputFocusAreaRef}
              width="100%"
              position={conversationId === undefined ? 'relative' : undefined}
            >
              <AIAssistantConversationInput
                conversationId={conversationId}
                autoFocus={autoFocus}
                setIsExitAnimationActive={setIsExitAnimationActive}
                onThreadInputFocus={handleThreadInputFocus}
                onThreadInputBlur={handleThreadInputBlur}
                isExitAnimationActive={isExitAnimationActive}
              />
              {conversationId === undefined && (
                <Layout
                  opacity={0}
                  className={
                    showSuggestedPrompts && !isExitAnimationActive
                      ? 'prompt-suggestions-entrance-animation'
                      : isExitAnimationActive
                        ? 'prompt-suggestions-exit-animation'
                        : ''
                  }
                  position="absolute"
                  top={80}
                  left={0}
                  width="100%"
                  height="100%"
                  flex
                  justify="center"
                  zIndex={showSuggestedPrompts ? 1 : -1}
                >
                  <Layout
                    className="ai-assistant-suggestion-pills"
                    width="100%"
                    direction="row"
                    gap={8}
                    overflow="auto"
                  >
                    {suggestedPrompts.map((prompt, index) => (
                      <AIAssistantPromptSuggestionCard
                        key={prompt}
                        prompt={prompt}
                        setIsExitAnimationActive={setIsExitAnimationActive}
                        paddingLeft={index === 0 ? 24 : 0}
                        paddingRight={index === suggestedPrompts.length - 1 ? 24 : 0}
                      />
                    ))}
                  </Layout>
                </Layout>
              )}
            </Layout>
          </Layout>
          {!conversationId && (
            <Layout
              __ref={widgetsContainerRef}
              marginTop={36}
              width="100%"
              paddingX={APP_PAGE_PADDING_X_MOBILE}
              paddingBottom={bottomNavigationHeight}
              className={widgetsAnimationClassName}
            >
              <Widgets />
            </Layout>
          )}
        </Layout>
      </Layout>
    );
  }

  return (
    <Layout
      width="100%"
      direction="row"
      justify="center"
      align="center"
      color={conversationId === undefined ? 'blue-50' : 'white'}
      flexGrow={1}
      className={isConversationsDrawerVisible ? 'content-shift' : 'content-reset'}
    >
      <KickoffModal isVisible={showKickoffModal} onClose={() => setShowKickoffModal(false)} />
      <OnboardingChecklistWelcomeModal
        isVisible={showOnboardingChecklistModal}
        onClose={handleCloseChecklistModal}
        onSkip={handleSkipChecklist}
      />
      {!isConversationsDrawerVisible && (
        <Layout position="absolute" top={24 + (appBannerType ? APP_BANNER_HEIGHT : 0)} left={84} gap={12}>
          <IconButton
            iconColor="gray-400"
            name="sideMenu"
            stroke={2}
            size="md"
            onClick={() => dispatch(actions.setSidebarVisible(true))}
            type="noBackground"
          />
          {conversationId !== undefined && (
            <IconButton
              iconColor="gray-400"
              name="createPen"
              stroke={2}
              size="md"
              onClick={() => history.push(AI_ASSISTANT_PATH)}
              type="noBackground"
            />
          )}
        </Layout>
      )}
      <AIAssistantConversations
        sideBarIsAlreadyShown={sideBarIsAlreadyShown}
        isVisible={isConversationsDrawerVisible}
        onClose={() => {
          setSideBarIsAlreadyShown(false);
          dispatch(actions.setSidebarVisible(false));
        }}
        loadedFirstTime={loadedFirstTime}
        setLoadedFirstTime={setLoadedFirstTime}
      />
      <Layout
        direction="column"
        width="100%"
        justify={conversationId ? 'space-between' : 'center'}
        align="center"
        height="100%"
      >
        {conversationId ? (
          <AIAssistantConversation conversationId={conversationId} onThreadsRendered={handleConversationsRendered} />
        ) : (
          <Layout
            direction="column"
            width="100%"
            justify="center"
            align="center"
            className={`greeting-layout ${isExitAnimationActive ? 'exit' : ''}`}
          >
            <Text size="body-xl" bold align="center">
              Hi, {user?.firstname}.
            </Text>
            <Text size="body-xl" bold color="purple-gradient">
              {greeting}
            </Text>
          </Layout>
        )}
        <Layout
          marginTop={conversationInputContainerMarginTop}
          width={768}
          maxWidth={768}
          marginBottom={conversationInputContainerMarginBottom}
          position={conversationId === undefined ? 'relative' : undefined}
        >
          <Layout __ref={threadInputFocusAreaRef} position="relative" width="100%">
            <AIAssistantConversationInput
              conversationId={conversationId}
              setIsExitAnimationActive={setIsExitAnimationActive}
              isExitAnimationActive={isExitAnimationActive}
              onThreadInputFocus={handleThreadInputFocus}
              onThreadInputBlur={handleThreadInputBlur}
              autoFocus={autoFocus}
            />
            {conversationId === undefined && (
              <Layout
                opacity={0}
                className={
                  showSuggestedPrompts && !isExitAnimationActive
                    ? 'prompt-suggestions-entrance-animation'
                    : isExitAnimationActive
                      ? 'prompt-suggestions-exit-animation'
                      : ''
                }
                position="absolute"
                top={80}
                left={0}
                width="100%"
                height="100%"
                flex
                direction="row"
                gap={8}
                justify="center"
                zIndex={showSuggestedPrompts ? 1 : -1}
              >
                {suggestedPrompts.map(prompt => (
                  <AIAssistantPromptSuggestionCard
                    key={prompt}
                    prompt={prompt}
                    setIsExitAnimationActive={setIsExitAnimationActive}
                  />
                ))}
              </Layout>
            )}
          </Layout>
          {!conversationId && (
            <Layout __ref={widgetsContainerRef} marginTop={36} className={widgetsAnimationClassName}>
              <Widgets />
            </Layout>
          )}
        </Layout>
      </Layout>
    </Layout>
  );
};

export default AIAssistant;
